/**
 * Ellipses Menu Dropdown Component
 * Displays application states and job actions in a dropdown menu
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { APPLICATION_STATES } from '../../../../../components/application-state';
import { Icon } from '../../../../../components/icons/Icon';
import type { ApplicationState } from '../../../../../redux/features/jobs/jobsSlice';
import { applicationStateMenuRowClasses } from '../utils/job-detail-action-chrome';

export interface EllipsesMenuDropdownProps {
  isOpen: boolean;
  currentApplicationState: ApplicationState | null;
  isApplied: boolean;
  isHidden: boolean;
  onSetApplicationState: (state: ApplicationState | null) => void;
  onToggleApplied: () => void;
  onToggleHide: () => void;
  onClose: () => void;
}

export const EllipsesMenuDropdown: React.FC<EllipsesMenuDropdownProps> = ({
  isOpen,
  currentApplicationState,
  isApplied,
  isHidden,
  onSetApplicationState,
  onToggleApplied,
  onToggleHide,
  onClose,
}) => {
  const intl = useIntl();

  if (!isOpen) return null;

  return (
    <div className="absolute right-0 top-full mt-2 w-48 rounded-xl shadow-lg bg-jc-surface border border-jc-border z-[9999] overflow-hidden">
      {APPLICATION_STATES.map((stateConfig) => {
        const isActive = currentApplicationState === stateConfig.value;
        const iconName = isActive ? stateConfig.iconSolid : stateConfig.icon;
        const iconVariant = isActive ? 'solid' : 'outline';
        const rowClass = applicationStateMenuRowClasses(stateConfig.color, isActive);
        return (
          <button
            key={stateConfig.value || 'none'}
            type="button"
            className={rowClass}
            onClick={() => {
              if (isActive) {
                onSetApplicationState(null);
              } else {
                onSetApplicationState(stateConfig.value);
              }
              onClose();
            }}
          >
            <Icon name={iconName} variant={iconVariant} useMaskForColor className={`h-5 w-5 ${isActive ? '' : 'opacity-60'}`} />
            <span className="flex-1">
              {isActive ? (
                <span className="font-medium">
                  {intl.formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel })} ✓
                </span>
              ) : (
                intl.formatMessage(
                  { id: 'hit.markAsState', defaultMessage: 'Mark as {state}', description: 'Menu item label template for marking job with a specific application state, where {state} is the state name (e.g., Applied, Interviewing)' },
                  { state: intl.formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }) }
                )
              )}
            </span>
          </button>
        );
      })}

      <div className="border-t border-jc-border my-1" />

      <button
        type="button"
        className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors cursor-pointer ${
          isApplied
            ? 'bg-jc-filter-salary-bg text-jc-filter-salary-text font-medium'
            : 'text-jc-ink-secondary hover:bg-jc-elevated'
        }`}
        onClick={() => {
          onToggleApplied();
          onClose();
        }}
      >
        <Icon name="checkCircle" variant={isApplied ? 'solid' : 'outline'} useMaskForColor className={`h-5 w-5 ${isApplied ? '' : 'opacity-60'}`} />
        <span className="flex-1">
          {isApplied
            ? intl.formatMessage({ id: 'hit.removeApplied', defaultMessage: 'Remove from applied' })
            : intl.formatMessage({ id: 'hit.markAsApplied.menu', defaultMessage: 'Mark as Applied', description: 'Menu item label to mark job as applied' })}
        </span>
      </button>

      <button
        type="button"
        className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 transition-colors cursor-pointer ${
          isHidden ? 'bg-jc-elevated text-jc-on-elevated font-medium' : 'text-jc-ink-secondary hover:bg-jc-elevated'
        }`}
        onClick={() => {
          onToggleHide();
          onClose();
        }}
      >
        <Icon name="eyeSlash" variant="outline" useMaskForColor className={`h-5 w-5 ${isHidden ? '' : 'opacity-60'}`} />
        <span className="flex-1">
          {isHidden
            ? intl.formatMessage({ id: 'hit.unhide', defaultMessage: 'Unhide job', description: 'Menu item label to unhide a previously hidden job' })
            : intl.formatMessage({ id: 'hit.hide.menu', defaultMessage: 'Hide', description: 'Menu item label to hide a job' })}
        </span>
      </button>
    </div>
  );
};
